import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink, Star } from 'lucide-react';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/5Z2mmmU49Mvmjq9QJs21jD/Captura_de_pantalla_2026-05-15_a_las_17.15.09-removebg-preview.png';
const STOREFRONT_IMG = 'https://cdn.magicpatterns.com/uploads/2dfDRfDfV9m3czZBNpcntj/IMG_1807.jpg';
const ACCENT_IMG = 'https://cdn.magicpatterns.com/uploads/drG4ovzY9Q4pegA384M7DP/IMG_1800.jpg';
const MENU_URL = 'https://smartmenu.agorapos.com/?id=7iud3xn2#/category/10';
export function Hero() {
  return <section className="relative bg-cafe-bg overflow-hidden" data-id="element-129">
      {/* Decorative warm glow */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-130">
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-cafe-accent/8 blur-3xl" data-id="element-131" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-cafe-card/60 blur-3xl" data-id="element-132" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-16 lg:pb-24" data-id="element-133">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-id="element-134">
          {/* Left: Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} className="lg:col-span-6 xl:col-span-7 text-center lg:text-left order-2 lg:order-1" data-id="element-135">
            {/* Logo */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7
          }} className="mb-8" data-id="element-136">
              <img src={LOGO_URL} alt="Trastienda Café" className="w-full max-w-sm lg:max-w-md mx-auto lg:mx-0 object-contain" data-id="element-137" />
            </motion.div>
            {/* Est. 2021 chip */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.3
          }} className="inline-flex items-center gap-2 bg-cafe-card text-cafe-primary px-4 py-2 rounded-full mb-6 text-xs tracking-[0.2em] uppercase font-medium" data-id="element-138">
              <Star size={12} className="text-cafe-accent fill-cafe-accent" data-id="element-139" />
              Est. 2021 · Tu café de barrio
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.35,
            duration: 0.6
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif text-cafe-primary leading-[1.05] mb-6" data-id="element-140">
              El sabor de los{' '}
              <span className="italic text-cafe-accent" data-id="element-141">buenos momentos</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45,
            duration: 0.6
          }} className="text-lg md:text-xl text-cafe-text/75 mb-10 leading-relaxed max-w-xl mx-auto lg:mx-0" data-id="element-142">
              Churros artesanos, crepes, tostas y batidos. Un rincón acogedor
              donde el tiempo se detiene y cada visita se siente como volver a
              casa.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6,
            duration: 0.6
          }} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-143">
              <Link to="/reservas" className="inline-flex justify-center items-center px-8 py-4 text-base font-medium rounded-full text-white bg-cafe-primary hover:bg-cafe-accent transition-all shadow-soft hover:shadow-lg transform hover:-translate-y-0.5 duration-200" data-id="element-144">
                Reservar mesa
              </Link>
              <a href={MENU_URL} target="_blank" rel="noopener noreferrer" className="inline-flex justify-center items-center px-8 py-4 text-base font-medium rounded-full text-cafe-primary bg-transparent border-2 border-cafe-primary hover:bg-cafe-primary hover:text-white transition-colors duration-200" data-id="element-145">
                Ver menú <ExternalLink size={18} className="ml-2" data-id="element-146" />
              </a>
            </motion.div>
          </motion.div>
          {/* Right: Editorial image composition */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.9,
          delay: 0.2
        }} className="lg:col-span-6 xl:col-span-5 order-1 lg:order-2 relative" data-id="element-147">
            <div className="relative aspect-[4/5] max-w-md mx-auto lg:max-w-none" data-id="element-148">
              {/* Decorative warm panel behind main image */}
              <div className="absolute -top-4 -right-4 w-full h-full bg-cafe-accent/20 rounded-3xl rotate-3 transform" data-id="element-149" />
              {/* Main image */}
              <div className="relative w-full h-full rounded-3xl overflow-hidden shadow-2xl" data-id="element-150">
                <img src={STOREFRONT_IMG} alt="Fachada de Trastienda Café con su cartel iluminado" className="w-full h-full object-cover" data-id="element-151" />
                {/* Subtle warm wash to unify with palette */}
                <div className="absolute inset-0 bg-gradient-to-t from-cafe-primary/30 via-transparent to-transparent" data-id="element-152" />
              </div>
              {/* Floating accent image (polaroid style) */}
              <motion.div initial={{
              opacity: 0,
              y: 20,
              rotate: 0
            }} animate={{
              opacity: 1,
              y: 0,
              rotate: -6
            }} transition={{
              delay: 0.9,
              duration: 0.7
            }} className="hidden md:block absolute -bottom-12 -left-10 w-44 bg-white p-2 pb-3 rounded-md shadow-xl" data-id="element-153">
                <img src={ACCENT_IMG} alt="Churro espiral artesano con chocolate y M&Ms" className="w-full aspect-square object-cover rounded-sm" data-id="element-154" />
                <p className="text-center text-[10px] font-serif italic text-cafe-primary mt-2 tracking-wider" data-id="element-155">
                  — Producto estrella —
                </p>
              </motion.div>
              {/* Floating rating chip */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 1.1,
              duration: 0.5
            }} className="absolute top-6 -left-4 md:-left-8 bg-white px-4 py-3 rounded-2xl shadow-xl flex items-center gap-3" data-id="element-156">
                <div className="flex" data-id="element-157">
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} className="text-cafe-accent fill-cafe-accent" data-id="element-158" />)}
                </div>
                <div className="text-xs" data-id="element-159">
                  <div className="font-semibold text-cafe-primary leading-tight" data-id="element-160">
                    Tu rincón favorito
                  </div>
                  <div className="text-cafe-text/60 leading-tight" data-id="element-161">
                    Café · Churros · Crepes
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}